import {CommonModule} from '@angular/common';
import {Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, inject} from '@angular/core';
import {FileWidget} from '@core/components/file-widget/file-widget';
import {Attachment, TaskItem} from '@core/types';
import {AttachmentService, TaskService} from '@core/services';
import {CompanyStateService} from '@core/states';

@Component({
  selector: 'core-task-attachments',
  standalone: true,
  imports: [
    CommonModule,
    FileWidget
  ],
  templateUrl: './task-attachments.html',
  styleUrl: './task-attachments.scss',
  host: {
    class: 'task-attachments-host'
  }
})
export class TaskAttachments {
  @Input() task: TaskItem | null = null;
  @Input() mode: 'chip' | 'button' | 'inline' = 'chip';
  @Input() allowUpload = true;
  @Input() showInlineList = false;
  @Output() taskUpdated = new EventEmitter<TaskItem>();
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  isOpen = false;
  isUploading = false;
  deletingAttachmentIds = new Set<string | number>();
  errorMessage = '';

  private readonly attachmentService = inject(AttachmentService);
  private readonly taskService = inject(TaskService);
  private readonly companyState = inject(CompanyStateService);
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  get attachments() {
    return this.task?.attachments || [];
  }

  get hasAttachments() {
    return this.attachments.length > 0;
  }

  get companyId() {
    const companyId = this.companyState.membership()?.company?.id;
    return companyId ? String(companyId) : '';
  }

  get triggerLabel() {
    if (this.isUploading) {
      return this.mode === 'button' ? 'Adjuntando...' : 'Actualizando...';
    }

    if (this.mode === 'button') {
      return this.hasAttachments ? `Adjuntos (${this.attachments.length})` : 'Adjuntar documentos';
    }
    return `Adjuntos (${this.attachments.length})`;
  }

  get shouldRenderTrigger() {
    if (this.mode === 'inline') {
      return false;
    }

    if (this.mode === 'button') {
      return true;
    }

    return this.hasAttachments;
  }

  toggleMenu(event?: Event) {
    event?.stopPropagation();
    if (this.mode === 'button') {
      this.openUploader(event);
      return;
    }
    if (!this.shouldRenderTrigger) return;
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.errorMessage = '';
    }
  }

  openUploader(event?: Event) {
    event?.stopPropagation();
    this.fileInput?.nativeElement.click();
  }

  async handleAttachmentSelection(event: Event) {
    const task = this.task;
    if (!task?.id || this.isUploading) return;

    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    input.value = '';
    if (!files.length) return;

    this.isUploading = true;
    this.errorMessage = '';
    const response = await this.attachmentService.uploadAttachments({
      companyId: this.companyId,
      files,
      relatedToType: 'task',
      relatedToId: task.id,
      skipLoader: true
    });
    this.isUploading = false;

    if (!response.success) {
      this.errorMessage = response.message || 'No se pudieron subir los adjuntos.';
      return;
    }

    const optimisticAttachments = files.map((file, index) => ({
      '@context': '',
      '@id': '',
      '@type': '',
      id: `temp-${task.id}-${Date.now()}-${index}`,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type
    }));
    task.attachments = [...(task.attachments || []), ...optimisticAttachments];
    this.taskUpdated.emit({...task, attachments: [...(task.attachments || [])]});

    await this.refreshTask();
    this.isOpen = true;
  }

  async downloadAttachment(attachment: Attachment) {
    if (!attachment?.id) return;

    const response = await this.attachmentService.downloadAttachment(attachment.id, undefined, {skipLoader: true});
    if (!response.success || !response.result) {
      this.errorMessage = response.message || 'No se pudo descargar el adjunto.';
      return;
    }

    const url = URL.createObjectURL(response.result);
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.fileName || 'adjunto';
    link.click();
    URL.revokeObjectURL(url);
  }

  async removeAttachment(attachment: Attachment) {
    const task = this.task;
    if (!task || !attachment?.id || this.deletingAttachmentIds.has(attachment.id)) return;

    this.deletingAttachmentIds.add(attachment.id);
    this.errorMessage = '';
    const response = await this.attachmentService.deleteAttachment(attachment.id, {skipLoader: true});
    this.deletingAttachmentIds.delete(attachment.id);

    if (!response.success) {
      this.errorMessage = response.message || 'No se pudo eliminar el adjunto.';
      return;
    }

    task.attachments = (task.attachments || []).filter(item => String(item.id) !== String(attachment.id));
    this.taskUpdated.emit({...task, attachments: [...(task.attachments || [])]});
    await this.refreshTask();
  }

  isDeletingAttachment(attachmentId: string | number) {
    return this.deletingAttachmentIds.has(attachmentId);
  }

  formatFileSize(size?: number): string {
    if (!size) return '0 KB';
    if (size < 1024) return `${size} B`;
    const kb = size / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
  }

  @HostListener('document:click', ['$event'])
  handleClickOutside(event: MouseEvent) {
    if (!this.isOpen) return;
    if (this.elementRef.nativeElement.contains(event.target as Node)) return;
    this.isOpen = false;
  }

  private async refreshTask() {
    if (!this.task?.id) return;

    const response = await this.taskService.getTask(this.task.id, {skipLoader: true});
    if (!response.success || !response.result) {
      return;
    }

    const currentAttachments = this.task.attachments || [];
    const nextAttachments = response.result.attachments || [];
    const mergedTask: TaskItem = {
      ...response.result,
      attachments: nextAttachments.length ? nextAttachments : currentAttachments
    };

    Object.assign(this.task, mergedTask);
    this.taskUpdated.emit(mergedTask);
  }
}
